// @ts-strict-ignore
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { PromiseQueue } from 'rainbow-web-sdk/utils/promiseQueue';
import { CallDirection, CallRB, CallRedirectionCause, CallStatus } from 'rainbow-web-sdk/models/call.model';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { Office365AdService } from 'rainbow-web-sdk/services/directory/office365ad.service';
import { CompanyDirectoryService } from 'rainbow-web-sdk/services/company/company-directory.service';
import type { TelephonyService } from 'rainbow-web-sdk/services/telephony/telephony.service';
import { SettingsService } from '../core/settings.service';

/**
 * @class
 * @public
 * @name TelephonySipWiseEventHandler
 * @description
 * This class is used to represent the handler of SipWise telephony events
 */
export class TelephonySipWiseEventHandler {

    // Service dependencies
    private xmppService: XmppService = XmppService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private office365AdService: Office365AdService = Office365AdService.getInstance();
    private companyDirectoryService: CompanyDirectoryService = CompanyDirectoryService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();

    // Private members
    private messageHandlerRef: any = null;
    private overflowUpdatRef: any = null;
    private userConfigurationRef: any = null;
    private huntingGroupUpdateRef: any = null;
    private huntingGroupAdminUpdateRef: any = null;
    private welcomeServiceRef: any = null;
    private clirUpdatRef: any = null; //RQRAINB-10990
    private promiseQueue: PromiseQueue;

    // Static members
    private static CALLSERVICE_NS: string = "urn:xmpp:pbxagent:telephony:1";
    private static OVERFLOWUPDATE_NS = "urn:xmpp:pbxagent:overflowOnSubscriberUpdated:1";
    private static USERCONFIGURATION_NS = "urn:xmpp:rvcp:userConfiguration:1";
    private static HUNTINGGROUPUPDATE_NS = "jabber:iq:notification";
    private static HUNTINGGROUPADMINUPDATE_NS = "urn:xmpp:group:notification:0";
    private static WELCOMESERVICE_NS = "urn:xmpp:rvcp:welcomeServicesMode:1";
    private static CLIRUPDATE_NS = "urn:xmpp:rvcp:userConfiguration:1";

    /**
     * Constructor of TelephonySipWiseEventHandler instance
     * @public
     * @param {TelephonyService} telephonyService reference to the telephony service
     * @memberof TelephonySipWiseEventHandler
     */
    constructor(
        private telephonyService: TelephonyService) {
        this.promiseQueue = PromiseQueue.create();
    }

    /**
     * Create the handler of telephony events for hybrid telephony (OXE / OXO / third party systems)
     * @public
     * @static
     * @param {any} telephonyService reference to the telephony service
     * @returns {TelephonySipWiseEventHandler} Instance of newly created TelephonyHybridEventHandler
     * @memberof TelephonySipWiseEventHandler
     */
    public static create(telephonyService: any) {
        return new TelephonySipWiseEventHandler(telephonyService);
    }

    CallFailureLabels = {
        invalidNumberFormat: "dialedNumberInvalid",
        destOutOfOrder: "outOfService",
        destNotObtainable: "outOfService",
        doNotDisturb: "dnd",
        busy: "correspondentIsBusy",
        incompatibleDestination: "incompatibleDestination",
        resourcesNotAvailable: "numberNotReachable",
        callCancelled: "numberNotReachable",
        unknownOverflow: "outgoingCallError"
    };

    /*************************************************************/
    /*          XMPP HANDLER ATTACH / DETACH                     */
    /*************************************************************/

    /**
     * Attach the xmpp handlers to callbacks
     * @public
     * @returns {void}
     * @memberof TelephonySipWiseEventHandler
     */
    public attachHandlers(): void {
        this.detachHandlers();
        this.messageHandlerRef = this.xmppService.addHandler((stanza) => { this.onCallserviceMessageReceived(stanza); return true; }, TelephonySipWiseEventHandler.CALLSERVICE_NS, "message", null);
        this.overflowUpdatRef = this.xmppService.addHandler((stanza) => { this.onOverflowUpdateMessageReceived(stanza); return true; }, TelephonySipWiseEventHandler.OVERFLOWUPDATE_NS, "message", null);
        this.userConfigurationRef = this.xmppService.addHandler((stanza) => { this.onUserConfigurationMessageReceived(stanza); return true; }, TelephonySipWiseEventHandler.USERCONFIGURATION_NS, "message", null);
        this.welcomeServiceRef = this.xmppService.addHandler((stanza) => { this.onWelcomeServiceMessageReceived(stanza); return true; }, TelephonySipWiseEventHandler.WELCOMESERVICE_NS, "message", null);
        this.huntingGroupUpdateRef = this.xmppService.addHandler((stanza) => { this.onhuntingGroupUpdateMessageReceived(stanza); return true; }, null, "message", "groupchat");
        this.huntingGroupAdminUpdateRef = this.xmppService.addHandler((stanza) => { this.onHGAdminUpdateMessageReceived(stanza); return true; }, TelephonySipWiseEventHandler.HUNTINGGROUPADMINUPDATE_NS, "message", null);
        this.clirUpdatRef = this.xmppService.addHandler((stanza) => { this.onClirMessageReceived(stanza); return true; }, TelephonySipWiseEventHandler.CLIRUPDATE_NS, "message", null); //RQRAINB-10990
    }

    /**
     * Detach the xmpp handlers
     * @public
     * @returns {void}
     * @memberof TelephonySipWiseEventHandler
     */
    public detachHandlers(): void {
        if (this.messageHandlerRef) {
            this.xmppService.deleteHandler(this.messageHandlerRef);
            this.messageHandlerRef = null;
        }
        if (this.overflowUpdatRef) {
            this.xmppService.deleteHandler(this.overflowUpdatRef);
            this.overflowUpdatRef = null;
        }
        if (this.userConfigurationRef) {
            this.xmppService.deleteHandler(this.userConfigurationRef);
            this.userConfigurationRef = null;
        }
        if (this.huntingGroupUpdateRef) {
            this.xmppService.deleteHandler(this.huntingGroupUpdateRef);
            this.huntingGroupUpdateRef = null;
        }
        if (this.huntingGroupAdminUpdateRef) {
            this.xmppService.deleteHandler(this.huntingGroupAdminUpdateRef);
            this.huntingGroupAdminUpdateRef = null;
        }
        if (this.welcomeServiceRef) {
            this.xmppService.deleteHandler(this.welcomeServiceRef);
            this.welcomeServiceRef = null;
        }
        if (this.clirUpdatRef) {
            this.xmppService.deleteHandler(this.clirUpdatRef);
            this.clirUpdatRef = null;
        }
    }

    /*************************************************************/
    /* EVENT HANDLERS                                            */
    /*************************************************************/

    /**
     * Main handling callback method for received telephony events
     * @public
     * @param {any} stanza Received telephony event
     * @returns {void}
     * @memberof TelephonySipWiseEventHandler
     */
    public onCallserviceMessageReceived(stanza: any): void {
        try {
            //console.log(stanza);
            const stanzaElem = $(stanza);

            const supervisionMessage = stanzaElem.find("forwarded[xmlns='urn:xmpp:supervision:0']");
            if (supervisionMessage?.length) {
                // this is a supervision message broadcasted to supervisors: it will be handled in supervision service and must be ignored here
                this.logger.debug("[TelephonySipWiseEventHandler] onCallserviceMessageReceived -- supervision Message => IGNORED");
                return;
            }

            // Extract global payload element
            //sipWise+ : new balise / callservice replaced by telephony
            const actionElem = stanzaElem.find("telephony");
            const forwardUpdatedElem = actionElem.find("forwardUpdated");
            if (forwardUpdatedElem && forwardUpdatedElem.length !== 0) {
                this.onforwardUpdatedMsg(forwardUpdatedElem);
                return;
            }
            //Else check telephony event
            const eventElem = actionElem.find("event");
            const seqNum = eventElem.attr("seqNum");
            if (seqNum) { //seqNum ctrl to be implemented ?
                const ts = eventElem.attr("ts"); //timestamp
                const cause = eventElem.attr("cause"); //cause
                this.logger.info("[TelephonySipWiseEventHandler] onCallserviceMessageReceived -- telephony event received and queued: seqNum = " + seqNum + " cause = " + cause + " ts = " + ts);
            } else {
                this.logger.info("[TelephonySipWiseEventHandler] onCallserviceMessageReceived -- no telephony event received then msg ignored");
                return;
            }
            // Push the telephony event in the message queue
            this.promiseQueue.add(() => { return this.onTelephonyEvent(eventElem, seqNum); });
        } catch (error) {
            this.logger.error("[TelephonySipWiseEventHandler] onCallserviceMessageReceived -- failure -- " + error.message);
        }
    }

    /**
     * Main handling callback method for overflowUpdateMessage events
     * @public
     * @param {any} stanza Received overflow event
     * @returns {void}
     * @memberof TelephonySipWiseEventHandler
     */
    public onOverflowUpdateMessageReceived(stanza: any): void {
        try {
            const stanzaElem = $(stanza);
            // Extract global payload element
            const overflowOnSubscriberUpdated = stanzaElem.find("overflowOnSubscriberUpdated");
            const overflowUpdated = overflowOnSubscriberUpdated.find("overflowUpdated");
            if (overflowUpdated && overflowUpdated.length !== 0) {
                this.overflowUpdatedMsg(overflowUpdated);
            }
        } catch (error) {
            this.logger.error("[TelephonySipWiseEventHandler] onOverflowUpdateMessageReceived -- failure -- " + error.message);
        }
    }//end of onOverflowUpdateMessageReceived

    /**
     * callback method for user configuation events
     * @public
     * @param {any} stanza Received configuration event
     * @returns {void}
     * @memberof TelephonySipWiseEventHandler
     */
    public onUserConfigurationMessageReceived(stanza: any): void {
        try {
            const stanzaElem = $(stanza);
            //Extract various global payload element
            const routingElt = stanzaElem.find("routing");
            if (routingElt && routingElt.length !== 0) {
                this.onRoutingUpdatedMsg(routingElt);
            }
            const cliUpdatedElt = stanzaElem.find("cliUpdated");
            if (cliUpdatedElt && cliUpdatedElt.length !== 0) {
                this.onCliUpdatedMsg(cliUpdatedElt);
            }
        } catch (error) {
            this.logger.error("[TelephonySipWiseEventHandler] onUserConfigurationMessageReceived -- failure -- " + error.message);
        }
    }//end of onUserConfigurationMessageReceived

    /**
     * callback method for user configuation events
     * @public
     * @param {any} stanza Received configuration event
     * @returns {void}
     * @memberof TelephonySipWiseEventHandler
     */
    public onWelcomeServiceMessageReceived(stanza: any): void {
        try {
            const stanzaElem = $(stanza);
            //Extract various global payload element
            const welcomeServiceElt = stanzaElem.find("welcomeService");
            if (welcomeServiceElt && welcomeServiceElt.length !== 0) {
                const welcomeServiceMode = welcomeServiceElt.find("mode").text();
                const welcomeServiceId = welcomeServiceElt.find("id").text();
                const welcomeServiceStartDate = welcomeServiceElt.find("startDate").text();
                const welcomeServiceEndDate = welcomeServiceElt.find("EndDate").text();
            }
        } catch (error) {
            this.logger.error("[TelephonySipWiseEventHandler] onWelcomeServiceMessageReceived -- failure -- " + error.message);
        }
    }//end of onWelcomeServiceMessageReceived

    /**
     * callback method for hunting group events
     * @public
     * @param {any} stanza Received update event
     * @returns {void}
     * @memberof TelephonySipWiseEventHandler
     */
    public onhuntingGroupUpdateMessageReceived(stanza: any): void {
        try {
            const stanzaElem = $(stanza);
            //Extract various global payload element
            const rvcpGroupElt = stanzaElem.find("rvcpgroup");
            const memberData = rvcpGroupElt.find("member");
            const memberStatus = memberData.attr("status");
            if (rvcpGroupElt && rvcpGroupElt.length !== 0) {
                if (rvcpGroupElt.attr("event")) {
                    this.logger.info("[telephonySipWiseEventHandler] onhuntingGroupUpdateMessageReceived -- update HG received -- ");
                    this.logger.info("[telephonySipWiseEventHandler] onhuntingGroupUpdateMessageReceived -- for memberId/role/status: " + memberData.attr("memberId") + "/" + memberData.text() + "/" + memberStatus);
                    this.eventService.publish("ON_SIPWISE_HUNTING_GROUP_UPDATE_EVENT");
                }
            }
            const groupElt = stanzaElem.find("group");
            const roomJID = groupElt.attr("jid");
            const realTimeElt = groupElt.find("realTime");
            const waitingCalls = realTimeElt.find("waitingCalls");
            const activeCalls = realTimeElt.find("activeCalls");
            if (waitingCalls?.length !== 0 && activeCalls?.length !== 0) {
                const realStatObj = { roomJID: roomJID, waitingCallsCount: waitingCalls.attr("count"), waitingCallsOldestDate: waitingCalls.attr("oldestDate"), activeCallsCount: activeCalls.attr("count"), activeCallsOldestDate: activeCalls.attr("oldestDate") };
                this.logger.info("[telephonySipWiseEventHandler] onhuntingGroupUpdateMessageReceived -- realtime statistics -- JID: " + roomJID);
                this.eventService.publish("ON_SIPWISE_HUNTING_GROUP_REALTIME_STATISTICS_EVENT", realStatObj);
            }
        } catch (error) {
            this.logger.error("[TelephonySipWiseEventHandler] onhuntingGroupUpdateMessageReceived -- failure -- " + error.message);
        }
    }//end of onhuntingGroupUpdateMessageReceived

    /**
     * callback method for hunting group events
     * @public
     * @param {any} stanza Received admin update event
     * @returns {void}
     * @memberof TelephonySipWiseEventHandler
     */
    public onHGAdminUpdateMessageReceived(stanza: any): void { //CRRAINB-31219
        try {
            const stanzaElem = $(stanza);
            //Extract various global payload element
            const rvcpGroupElt = stanzaElem.find("rvcpgroup");
            const rvcpGroupAction = rvcpGroupElt.attr("action");
            if (rvcpGroupElt && rvcpGroupElt.length !== 0) {
                if (rvcpGroupAction && rvcpGroupAction !== "incoming-blacklist-updated") {
                    this.logger.info("[telephonySipWiseEventHandler] onHGAdminUpdateMessageReceived -- Admin HG update received -- ");
                    this.logger.info("[telephonySipWiseEventHandler] onHGAdminUpdateMessageReceived -- for groupId/action: " + rvcpGroupElt.attr("id") + "/" + rvcpGroupAction);
                    this.telephonyService.getSipWiseUserGroups();
                }
            }
        } catch (error) {
            this.logger.error("[TelephonySipWiseEventHandler] onHGAdminUpdateMessageReceived -- failure -- " + error.message);
        }
    }//end of onHGAdminUpdateMessageReceived

    /**
     * Main handling callback method for onClirMessage events
     * @public
     * @param {any} stanza Received clir event
     * @returns {void}
     * @memberof TelephonySipWiseEventHandler
     */
    public onClirMessageReceived(stanza: any): void { //RQRAINB-10990
        try {
            const stanzaElem = $(stanza);
            const clirElt = stanzaElem.find("clir");
            const clirUpdated = clirElt.find("clirUpdated");
            if (clirUpdated && clirUpdated.length !== 0) {
                this.onClirUpdatedMsg(clirUpdated);
            }
        } catch (error) {
            this.logger.error("[TelephonySipWiseEventHandler] onClirMessageReceived -- failure -- " + error.message);
        }
    }//end of onClirMessageReceived

    /**
     * Method onforwardUpdatedMsg
     * @private
     * @param {any} forwardUpdatedElem forwardUpdated sub element of received event
     * @returns {void}
     * @memberof TelephonySipWiseEventHandler
     */
    private onforwardUpdatedMsg(forwardUpdatedElem: any): void {
        this.logger.info("[TelephonySipWiseEventHandler] onforwardUpdatedMsg : msg forwardUpdated received");
        const forwardItem = forwardUpdatedElem.attr("forwardItem");
        if (forwardItem === "user") {
            this.telephonyService.getForwardStatus();
        }
        if (forwardItem === "group") {
            const groupId = forwardUpdatedElem.attr("forwardItemId");
            if (groupId) {
                this.telephonyService.getSipWiseUserGroupsForwards(groupId);
                if (this.telephonyService.sipWiseManAssistGetMyRole() === "manager") {
                    //if i'm manager then update also forward status
                    this.telephonyService.getForwardStatus();
                }
            } else {
                this.telephonyService.getSipWiseUserGroups();
            }
        }
    } //end onforwardUpdatedMsg

    /**
     * Method onRoutingUpdatedMsg
     * @private
     * @param {any} routingElem [required] xmpp msg, service part
     * @returns {void} NA
     * @memberof TelephonySipWiseEventHandler
     */
    private onRoutingUpdatedMsg(routingElem: any): void {
        this.logger.info("[TelephonySipWiseEventHandler] onRoutingUpdatedMsg : msg routing update received");
        const routingUpdatedElt = routingElem.find("routingUpdated");
        const currentDeviceId = routingUpdatedElt.find("currentDeviceId").text();
        const destinationElem = routingUpdatedElt.find("destinations");
        const destinations = [];
        const ringObj = this.telephonyService.telephonyServiceSipWise.routingData;

        if (!currentDeviceId || !destinationElem) {
            this.logger.info("[TelephonySipWiseEventHandler] onRoutingUpdatedMsg Anomaly: no current/destination elts");
            return;
        }
        ringObj.alsoOfficePhoneRingEnabled = false;
        destinationElem.find("destination").each((__index: number, destination: Element) => {
            const destinationElemIter = $(destination);
            const type = destinationElemIter.attr("type");
            const deviceId = destinationElemIter.attr("deviceId");
            this.logger.info("[TelephonySipWiseEventHandler] onRoutingUpdatedMsg ITER desti / type = " + type + " deviceId = " + deviceId);
            destinations.push({ deviceId: deviceId, type: type });
            if (type === "sip") {
                ringObj.alsoOfficePhoneRingEnabled = true;
            }
        });
        if (currentDeviceId) {
            this.telephonyService.telephonyServiceSipWise.setCurrentDevice(currentDeviceId);
            if (!this.telephonyService.nomadicObject.destination && this.telephonyService.telephonyServiceSipWise.getCurrentDevice().type === "webrtc") { //CRRAINB-47675
                this.telephonyService.telephonyServiceSipWise.patchNomadicData();
            }
        }
        if (destinations?.length) {
            this.logger.info("[TelephonySipWiseEventHandler] onRoutingUpdatedMsg  / ringing destinations nb  = " + destinations.length);
            ringObj.ringingDestinations = destinations;
            this.eventService.publish("ON_SIPWISE_ROUTING_EVENT", this.telephonyService.telephonyServiceSipWise.getCurrentDevice().type, ringObj);
        }
    } //end onRoutingUpdatedMsg

    /**
     * Method overflowUpdatedMsg
     * @private
     * @param {any} overflowUpdated [required] xmpp msg, service part
     * @returns {void} NA
     * @memberof TelephonySipWiseEventHandler
     */
    private overflowUpdatedMsg(overflowUpdated: any): void {
        this.logger.info("[TelephonySipWiseEventHandler] overflowUpdatedMsg received :" + (overflowUpdated !== null));
        this.telephonyService.getForwardStatus();//CRRAINB-13692
    } //end overflowUpdatedMsg

    /**
     * Method onCliUpdatedMsg
     * @private
     * @param {any} cliUpdatedElt [required] xmpp msg, service part
     * @returns {void} NA
     * @memberof TelephonySipWiseEventHandler
     */
    private onCliUpdatedMsg(cliUpdatedElt: any): void {
        this.logger.info("[TelephonyServiceEventHandlerSipWise] cliUpdatedMsg received");
        const policy = cliUpdatedElt.find("policy").text();
        const phoneNumberId = cliUpdatedElt.find("phoneNumberId").text();
        const number = cliUpdatedElt.find("number").text();

        if (!policy || !phoneNumberId || !number) {
            this.logger.info("[TelephonyServiceEventHandlerSipWise] onCliUpdatedMsg Anomaly: missing CLI para");
            return;
        }
        const cliObject = { policy: policy, phoneNumberId: phoneNumberId, number: number };
        this.telephonyService.getSipWiseAllCli(true); // Forcefully update local CLI data
        this.eventService.publish("ON_CLI_EVENT", cliObject);
    } //end onCliUpdatedMsg

    /**
     * Method onClirUpdatedMsg
     * @private
     * @param {any} clirUpdatedElt [required] xmpp msg, service part
     * @returns {void} NA
     * @memberof TelephonySipWiseEventHandler
     */
    private onClirUpdatedMsg(clirUpdatedElt: any): void { //RQRAINB-10990
        const anonymous = clirUpdatedElt.find("anonymous").text();
        this.logger.info("[TelephonyServiceEventHandlerSipWise] clirUpdatedMsg received / anonymous : " + anonymous);
        if (!anonymous) {
            this.logger.info("[TelephonyServiceEventHandlerSipWise] onClirUpdatedMsg Anomaly: missing anonymous para");
            return;
        }
        this.eventService.publish("ON_CLIR_EVENT", anonymous === "true");
    } //end onClirUpdatedMsg

    /**
     * Method onTelephonyEvent
     * search call elem in eventElem
     * @private
     * @param {any} eventElem [required] xmpp msg, event part
     * @param {string} seqNum [required] messqge sequence number
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonySipWiseEventHandler
     */
    private async onTelephonyEvent(eventElem: any, seqNum: string): Promise<void> {
        this.logger.info("[TelephonySipWiseEventHandler] onCallserviceMessageReceived seqNum " + seqNum);
        const callsToUpdate: CallRB[] = []; //tab of callId for call to be updated contain struc {trigger:boolean;info:string}
        try {
            await this.manageAllDevices(eventElem, seqNum);//CRRAINB-31749
            await this.manageAllCalls(eventElem, seqNum, callsToUpdate);
            await this.manageAllLegs(eventElem, seqNum, callsToUpdate);
            await this.manageAllEndPoints(eventElem, seqNum, callsToUpdate);
        } catch (error) {
            this.logger.info("[TelephonySipWiseEventHandler] [TEL][EVENT] onTelephonyEvent CATCH / seqNum = " + seqNum);
            const errorMessage = "onTelephonyEvent -- " + error.message;
            this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
        } finally {
            this.callsUpdate(callsToUpdate, seqNum);
        }
    } //end onTelephonyEvent

    /**
     * Method manageAllDevices
     * loop on all device evt
     * @private
     * @param {any} eventElem [required] xmpp msg, event part
     * @param {string} seqNum [required] messqge sequence number
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonySipWiseEventHandler
     */

    private async manageAllDevices(eventElem: any, seqNum: string): Promise<void> { //CRRAINB-31749
        const devicePromises = [];

        //context situation ctrl
        if (!(this.telephonyService.started)) {
            this.logger.info("[TelephonySipWiseEventHandler] manageAllDevices : device state received but telephonyService not started then ignore / seqNum = " + seqNum);
            return;//evt ignored
        }
        const currentDevice = this.telephonyService.telephonyServiceSipWise.getCurrentDevice();
        const routingData = this.telephonyService.telephonyServiceSipWise.routingData;
        if (!(currentDevice && routingData && routingData.devicesList.length > 0)) {
            this.logger.info("[TelephonySipWiseEventHandler] manageAllDevices : device state received but devices and routing not yet initialized then ignore / seqNum = " + seqNum);
            return;//evt ignored
        }

        eventElem.find("device").each((__index: number, device: Element) => {
            const deviceElemIter = $(device);
            const deviceId = deviceElemIter.attr("deviceId");

            this.logger.debug("[TelephonySipWiseEventHandler] manageAllDevices ITER deviceId = " + deviceId + " / seqNum = " + seqNum);
            //manage only the <device> with parent node -> <event> but not those from others sub-structure as <endpoint>
            if (device.parentNode && (device.parentNode.nodeName.toUpperCase() === "EVENT")) {
                devicePromises.push((async (): Promise<void> => {
                    try {
                        await this.manageDeviceElem(deviceElemIter, seqNum);
                    }
                    catch (error) {
                        this.logger.info("[TelephonySipWiseEventHandler] manageAllDevices CATCH / seqNum = " + seqNum);
                        const errorMessage = "manageAllDevices -- " + error.message;
                        this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
                    }
                })());
            }
        });
        // when all devices processed
        try {
            await Promise.all(devicePromises);
        } finally {
            this.eventService.publish("ON_SIPWISE_ROUTING_EVENT", currentDevice.type, routingData);
        }
    } //end manageAllDevices

    /**
     * Method manageDeviceElem
     * search leg elem in eventElem
     * @private
     * @param {any} deviceElem [required] leg part
     * @param {string} seqNum [required] messqge sequence number
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonySipWiseEventHandler
     */

    private async manageDeviceElem(deviceElem: any, seqNum: string): Promise<void> { //CRRAINB-31749
        try {
            //collecte attributes
            const deviceId = deviceElem.attr("deviceId"); //required
            const state = deviceElem.attr("state");

            // param ctrl
            if (!deviceId) {
                const errorMessage = "manageDeviceElem -- Anomaly deviceId missing / seqNum = " + seqNum;
                throw new Error(errorMessage);
            }
            if (!state) {
                const errorMessage = "manageDeviceElem -- Anomaly state missing / seqNum = " + seqNum;
                throw new Error(errorMessage);
            } else if (!(state === "inService" || state === "outOfService" || state === "unknown")) {
                const errorMessage = "manageDeviceElem -- Anomaly state not managed : deviceId = " + deviceId + " / state = " + state + " / seqNum = " + seqNum;
                throw new Error(errorMessage);
            }

            // device ctrl
            const device = this.telephonyService.telephonyServiceSipWise.routingData.devicesList.find(elt => elt.deviceId === deviceId);
            if (!device) {
                const errorMessage = "manageDeviceElem -- Anomaly unknown device : deviceId = " + deviceId + " / seqNum = " + seqNum;
                throw new Error(errorMessage);
            }
            this.logger.info("[TelephonySipWiseEventHandler] manageDeviceElem deviceId = " + deviceId + " / state = " + state + " /  seqNum = " + seqNum);
            device.state = state; // set the received state
            if (device.type === "webrtc") { //but force computer webrtc device to inService because not managed from infra
                device.state = "inService";
            }
        } catch (error) {
            const errorMessage = "managedeviceElem -- " + error.message;
            throw new Error(errorMessage);
        }
    } //end manageDeviceElem

    /**
     * Method manageAllCalls
     * loop on all calls
     * @private
     * @param {any} eventElem [required] xmpp msg, event part
     * @param {string} seqNum [required] messqge sequence number
     * @param {any[]} callsToUpdate [required] list of calls to update
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonySipWiseEventHandler
     */

    private async manageAllCalls(eventElem: any, seqNum: string, callsToUpdate: any[]): Promise<void> {
        const callPromises = [];
        let liveCalls = 0;

        eventElem.find("call").each((__index: number, callElem: Element) => {
            const callElemIter = $(callElem);
            const callId = callElemIter.attr("callId");
            const op = callElemIter.attr("op");
            this.logger.debug("[TelephonySipWiseEventHandler] manageAllCalls ITER callId = " + callId + " op = " + op + " / seqNum = " + seqNum);

            if (op !== "ended") { liveCalls++; }
            callPromises.push((async (): Promise<void> => {
                try {
                    await this.manageCallElem(callElemIter, eventElem, seqNum, callsToUpdate);
                }
                catch (error) {
                    this.logger.info("[TelephonySipWiseEventHandler] manageAllCalls CATCH / seqNum = " + seqNum);
                    const errorMessage = "manageAllCalls -- " + error.message;
                    this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
                }
            })());
        });

        // when all calls processed
        try {
            await Promise.all(callPromises);
        } finally {
            this.logger.info("[TelephonySipWiseEventHandler] manageAllCalls : nb living calls = " + liveCalls + " / seqNum = " + seqNum);
        }
    } //end manageAllCalls

    /**
     * Method manageCallElem
     * search call elem in eventElem
     * @private
     * @param {any} callElem [required] call part
     * @param {any} eventElem [required] xmpp msg, event part
     * @param {any} seqNum [required] messqge sequence number
     * @param {any[]} callsToUpdate [required] list of calls to update
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonySipWiseEventHandler
     */
    private async manageCallElem(callElem: any, eventElem: any, seqNum: string, callsToUpdate: any[]): Promise<void> {
        if (callElem.length) { //element Call exist
            try {
                //collecte all attributes
                const callId = callElem.attr("callId"); //required
                const oldCallId = callElem.attr("oldCallId");//option
                //const newCallId = callElem.attr("newCallId");//option
                const op = callElem.attr("op"); //required
                if (!callId) {
                    const errorMessage = "manageCallElem -- Anomaly callId missing / seqNum = " + seqNum;
                    this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
                    throw new Error(errorMessage);
                }
                if (!op) {
                    const errorMessage = "manageCallElem -- Anomaly op missing / seqNum = " + seqNum;
                    this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
                    throw new Error(errorMessage);
                }
                this.logger.info("[TelephonySipWiseEventHandler] manageCallElem callId = " + callId + " op = " + op + " / seqNum = " + seqNum);
                switch (op) { //only manage call end and update if existing (other case managed as leg/endpoint operations)
                    case "created": {
                        this.logger.info("[TelephonySipWiseEventHandler] manageCallElem / call creation / seqNum = " + seqNum);
                        const call: CallRB = this.telephonyService.createCallSipWise(CallStatus.ENDED, callId);
                        if (call) { //update call sipWiseData
                            const cause: string = eventElem.attr("cause"); //obtain initial cause
                            if (cause) { call.setInitCauseSipWiseData(cause); } //and store it
                            const direction: CallDirection = callElem.find("direction").text() as CallDirection;
                            if (direction) { call.setDirection(direction); }
                            //check if type exists more than once
                            if (callElem.find("type").length) {
                                const type: string = $(callElem.find("type")[0]).text();
                                if (type) { call.setTypeSipWiseData(type); }
                            }

                            this.setCalledParty(callElem, call);

                            const autoOffHookResource = callElem.find("autoOffHookResource").text();
                            if (autoOffHookResource) {
                                call.setAutoOffHookResource(autoOffHookResource);
                                this.logger.info("[TelephonySipWiseEventHandler] manageCallElem autoOffHookResource = " + autoOffHookResource + " callId = " + callId + " / seqNum = " + seqNum);
                            }
                            //CR25504-24670 & RQ6572 ++
                            if (call.getInitCauseSipWiseData() === "singleStepTransfer" && call.getAutoOffHookResource()) {
                                call.setMixtWebTelSingleStepTrans(true);
                                this.logger.info("[TelephonySipWiseEventHandler] manageCallElem mixtWebTelSingleStepTrans = true" + " for callId = " + callId + " / seqNum = " + seqNum);
                            }//CR25504-24670 & RQ6572 --
                        }
                        callsToUpdate[callId] = { trigger: false, info: "call" + op }; //store callId op
                        break;
                    }
                    case "updated": {
                        this.logger.info("[TelephonySipWiseEventHandler] manageCallElem / call update / seqNum = " + seqNum);

                        if (this.checkCallRelevancy(callId)) {
                            const call: CallRB = this.getCallFromCallId(callId);
                            if (call) {
                                const updatedCall: boolean = this.setCalledParty(callElem, call);
                                if (updatedCall) {
                                    callsToUpdate[callId] = { trigger: true, info: "call" + op }; //update op
                                }
                            }
                        } else {
                            this.logger.info("[TelephonySipWiseEventHandler] manageCallElem : on callId " + callId + " updated, not relevant");
                        }
                        break;
                    }
                    case "ended": {
                        this.logger.info("[TelephonySipWiseEventHandler] manageCallElem / call end / seqNum = " + seqNum);
                        if (this.checkCallRelevancy(callId)) { //tag callId as call to be updated
                            this.logger.info("[TelephonySipWiseEventHandler] manageCallElem : callId = " + callId + " ended " + " / seqNum = " + seqNum);
                            this.telephonyService.clearCall(this.getCallFromCallId(callId), true); //2nd para true for clearcall without broadcast
                            callsToUpdate[callId] = { trigger: true, info: "call" + op }; //update op
                        } else {
                            this.logger.info("[TelephonySipWiseEventHandler] manageCallElem : on callId " + callId + " ended, not relevant");
                        }
                        break;
                    }
                    default: {
                        this.logger.info("[TelephonySipWiseEventHandler] manageCallElem / invalid op -> " + op + " / seqNum = " + seqNum);
                        const errorMessage = "manageCallElem -- Anomaly invalid op / seqNum = " + seqNum;
                        this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
                        throw new Error(errorMessage);
                    }
                }
                if (oldCallId) { // CRRAINB-32192  : manage specific transfer case from oldCall to call
                    //for transfer cases : specific traitment is to delete old call / could occurs for create or update
                    //manage also mediaPillar context in case of MP call (computer mode)
                    const oldCall: CallRB = this.getCallFromCallId(oldCallId); // the old call
                    const newCall: CallRB = this.getCallFromCallId(callId); // the new call
                    if (oldCall) {
                        const mediaPillarCall = oldCall.getMediaPillarCall(); // the current (old) MP constext
                        if (mediaPillarCall) { //if there is a former MP context we must keep it for the new call
                            //place the new call in the previous MP context !! note : In sip case only telephonyCallRefs[0] is used
                            mediaPillarCall.telephonyCallRefs[0] = newCall;
                            oldCall.setMediaPillarCall(null); //in old call remove the mediapillarCall context link
                            newCall.setMediaPillarCall(mediaPillarCall); //in new call link the former MP context
                        }
                        this.telephonyService.clearCall(oldCall, true); //2nd para true for clearcall without broadcast
                        callsToUpdate[oldCallId] = { trigger: true, info: "call" + "ended" }; // flag the old call as be updated
                    } else {
                        this.logger.info("[TelephonySipWiseEventHandler] manageCallElem : oldCallId " + oldCallId + " ended, no call found");
                    }
                }
            } catch (error) {
                const errorMessage = "manageCallElem -- " + error.message;
                throw new Error(errorMessage);
            }
            return;
        }
        this.logger.info("[TelephonySipWiseEventHandler] manageCallElem / no CallElem");
    } //end manageCallElem

    private setCalledParty(callElem: any, call: CallRB): boolean {

        const calledElem = callElem.find("called");
        if (calledElem?.length) {
            // Parse called party
            const isCalledMySelf: boolean = calledElem.attr("myself") === "true";
            if (isCalledMySelf) return false;

            let calledPhoneNumber = calledElem.find("phoneNumber").text();
            let calledDisplayName = calledElem.find("displayName").text();
            let calledJid = calledElem.find("jid").text();
            let calledType = calledElem.find("type").text();
            //RQRAINB-9406++ when present, group info should replace the called info
            const groupInfo = callElem.find("group");
            //const groupInfo_type = groupInfo.attr("type");
            if (groupInfo?.length) {
                calledPhoneNumber = groupInfo.attr("number");
                calledDisplayName = groupInfo.attr("displayName");
                calledJid = groupInfo.attr("jid");
                calledType = groupInfo.attr("type");
            }
            //RQRAINB-9406--
            if (calledType === "hg" || calledType === "ccq") { call.redirectionCause = CallRedirectionCause.HUNTGROUP; }

            const calledNumberForLogs = calledPhoneNumber ? calledPhoneNumber.charAt(0) + calledPhoneNumber.replace(/[^\s](?=.{1,}$)/g, "*").substr(1) : "";
            this.logger.info(`[TelephonySipWiseEventHandler] manageCallElem / call creation -- calledNumber -- ${calledNumberForLogs}`);

            return this.telephonyService.setCalledParty(call, calledPhoneNumber, calledJid, calledDisplayName, "", "", (calledType === "hg" || calledType === "ccq") ? "calledGroup" : "called");
        }
        return false;
    }

    /**
     * Method manageAllLegs
     * loop on all legs
     * @private
     * @param {any} eventElem [required] xmpp msg, event part
     * @param {string} seqNum [required] messqge sequence number
     * @param {any[]} callsToUpdate [required] list of calls to update
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonySipWiseEventHandler
     */

    private async manageAllLegs(eventElem: any, seqNum: string, callsToUpdate: any[]): Promise<void> {
        const legPromises = [];
        let liveLegs = 0;

        eventElem.find("leg").each((__index: number, leg: Element) => {
            const legElemIter = $(leg);
            const legId = legElemIter.attr("legId");
            const op = legElemIter.attr("op");
            this.logger.info("[TelephonySipWiseEventHandler] manageAllLegs ITER legId = " + legId + " op = " + op + " / seqNum = " + seqNum);

            if (legElemIter.length) {
                if (op !== "ended") { liveLegs++; }
                legPromises.push((async (): Promise<void> => {
                    try {
                        await this.manageLegElem(legElemIter, eventElem, seqNum, callsToUpdate);
                    }
                    catch (error) {
                        this.logger.info("[TelephonySipWiseEventHandler] manageAllLegs CATCH / seqNum = " + seqNum);
                        const errorMessage = "manageAllLegs -- " + error.message;
                        this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
                    }
                })());
            }
        });
        // when all endPoints processed
        try {
            await Promise.all(legPromises);
        } finally {
            this.logger.info("[TelephonySipWiseEventHandler] manageAllLegs : nb living legs = " + liveLegs + " / seqNum = " + seqNum);
        }
    } //end manageAllLegs

    /**
     * Method manageLegElem
     * search leg elem in eventElem
     * @private
     * @param {any} legElem [required] leg part
     * @param {any} eventElem [required] xmpp msg, event part
     * @param {string} seqNum [required] messqge sequence number
     * @param {any[]} callsToUpdate [required] list of calls to update
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonySipWiseEventHandler
     */

    private async manageLegElem(legElem: any, eventElem: any, seqNum: string, callsToUpdate: any[]): Promise<void> {
        try {
            //collecte all attributes mainly for log
            const legId = legElem.attr("legId"); //required
            const callId = legElem.attr("callId"); //required
            const deviceId = legElem.attr("deviceId");
            const state = legElem.attr("state");
            const op = legElem.attr("op"); //required
            if (!legId) {
                const errorMessage = "manageLegElem -- Anomaly legId missing / seqNum = " + seqNum;
                throw new Error(errorMessage);
            }
            if (!callId) {
                const errorMessage = "manageLegElem -- Anomaly callId missing / seqNum = " + seqNum;
                throw new Error(errorMessage);
            }
            if (!op) {
                const errorMessage = "manageLegElem -- Anomaly op missing / seqNum = " + seqNum;
                throw new Error(errorMessage);
            }
            this.logger.info("[TelephonySipWiseEventHandler] manageLegElem legId = " + legId + " callId = " + callId
                + " deviceId = " + deviceId + " state = " + state + " op = " + op + " / seqNum = " + seqNum);
            switch (op) {
                case "created":
                    if (this.computeCallRelevancy(deviceId, callId)) {
                        this.logger.debug("[TelephonySipWiseEventHandler] manageLegElem / create legId/callId = " + legId + "/" + callId + " relevant");
                        await this.legElemCreate(eventElem);
                        const call = this.getCallFromCallId(callId);
                        call.setConnectionIdSipWise(legId);
                        callsToUpdate[callId] = { trigger: true, info: "leg" + op }; //tag callId as call to be updated
                    } else { //this leg is not to be managed
                        this.logger.info("[TelephonySipWiseEventHandler] manageLegElem / create legId/callId = " + legId + "/" + callId + " not relevant");
                    }
                    break;
                case "updated":
                    if (this.computeCallRelevancy(deviceId, callId)) {
                        this.logger.debug("[TelephonySipWiseEventHandler] manageLegElem / update legId/callId = " + legId + "/" + callId + " relevant");
                        await this.legElemUpdate(eventElem);
                        const call = this.getCallFromCallId(callId);
                        call.setConnectionIdSipWise(legId);
                        callsToUpdate[callId] = { trigger: true, info: "leg" + op }; //tag callId as call to be updated
                    } else { //this leg is not to be managed
                        this.logger.info("[TelephonySipWiseEventHandler] manageLegElem / update legId/callId = " + legId + "/" + callId + " not relevant");
                    }
                    break;
                case "ended":
                    this.logger.info("[TelephonySipWiseEventHandler] manageLegElem / leg end / seqNum = " + seqNum);
                    if (this.computeCallRelevancy(deviceId, callId)) {
                        //call state -> ended to be updated
                        const call = this.getCallFromCallId(callId);
                        if (call) {
                            this.telephonyService.clearCall(this.getCallFromCallId(callId), true); //2nd para true for clearcall without broadcast
                            callsToUpdate[callId] = { trigger: true, info: "leg" + op }; //update op
                        } else {
                            this.logger.info("[TelephonySipWiseEventHandler] manageLegElem : leg ended / call already removed");
                        }
                    } else { //this legs is no more involved in the call but not relevant device
                        this.logger.info("[TelephonySipWiseEventHandler] manageLegElem : leg ended but not relevant");
                    }
                    break;
                default: {
                    this.logger.info("[TelephonySipWiseEventHandler] manageLegElem / invalid op -> " + op + " / seqNum = " + seqNum);
                    const errorMessage = "manageLegElem -- Anomaly invalid op / seqNum = " + seqNum;
                    this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
                    throw new Error(errorMessage);
                }
            }
        } catch (error) {
            const errorMessage = "manageCallElem -- " + error.message;
            throw new Error(errorMessage);
        }
    } //end manageLegElem

    /**
     * Method manageAllEndPoints
     * manage all End points
     * @private
     * @param {any} eventElem [required] xmpp msg, event part
     * @param {string} seqNum [required] messqge sequence number
     * @param {any[]} callsToUpdate [required] list of calls to update
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonySipWiseEventHandler
     */

    private async manageAllEndPoints(eventElem: any, seqNum: string, callsToUpdate: any[]): Promise<void> {

        const endPointPromises = [];
        let liveEndPoints = 0;

        eventElem.find("endpoint").each((__index: number, endPointElem: Element) => {
            const endPointElemIter = $(endPointElem);
            //const endPointId = endPointElemIter.attr("endpointId");
            const op = endPointElemIter.attr("op");

            if (endPointElemIter.length) {
                if (op !== "ended") { liveEndPoints++; }
                endPointPromises.push((async (): Promise<void> => {
                    try {
                        await this.manageEndPointElem(endPointElemIter, eventElem, seqNum, callsToUpdate);
                    } catch (error) {
                        this.logger.info("[TelephonySipWiseEventHandler] manageAllEndPoints CATCH / seqNum = " + seqNum);
                        const errorMessage = "manageAllEndPoints -- " + error.message;
                        this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
                    }
                })());
            }
        });

        // when all endPoints processed
        try {
            await Promise.all(endPointPromises);
        } finally {
            this.logger.info("[TelephonySipWiseEventHandler] manageAllEndPoints : nb living endPoint = " + liveEndPoints + " / seqNum = " + seqNum);
        }
    } //end manageAllEndPoints

    /**
     * Method manageEndPointElem
     * search endPoint elem in eventElem
     * @private
     * @param {any} endPointElem [required] the end point
     * @param {any} eventElem [required] xmpp msg, event part
     * @param {string} seqNum [required] messqge sequence number
     * @param {any[]} callsToUpdate [required] list of calls to update
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonySipWiseEventHandler
     */

    private async manageEndPointElem(endPointElem: any, eventElem: any, seqNum: string, callsToUpdate: any[]): Promise<void> {
        try {
            //collect all attributes mainly for log
            const endPointId = endPointElem.attr("endpointId"); //required
            const callId = endPointElem.attr("callId"); //required
            //const state = endPointElem.attr("state"); //option
            const op = endPointElem.attr("op"); //required
            //const anonymous = endPointElem.attr("anonymous"); // boolean option

            if (!endPointId) {
                const errorMessage = "manageEndPointElem -- Anomaly endPointId missing / seqNum = " + seqNum;
                throw new Error(errorMessage);
            }
            if (!callId) {
                const errorMessage = "manageEndPointElem -- Anomaly callId missing / seqNum = " + seqNum;
                throw new Error(errorMessage);
            }
            if (!op) {
                const errorMessage = "manageEndPointElem -- Anomaly op missing / seqNum = " + seqNum;
                throw new Error(errorMessage);
            }

            //CR27947++
            let updateAnalyse: any = null;

            let updateStatus: any = null;
            const phoneNumber: string = endPointElem.find("phoneNumber").text();
            const call = this.getCallFromCallId(callId);
            const jid = endPointElem.find("jid").text();
            //CR27947--

            // Determine if the contact has to be updated from event informationconst endPointElem = eventElem.find("endpoint");
            switch (op) {
                case "created":
                    this.logger.info("[TelephonySipWiseEventHandler] manageEndPointElem: create endPointId/callId = " + endPointId + "/" + callId);

                    await this.endPointElemCreate(eventElem);
                    if (this.checkCallRelevancy(callId)) {
                        callsToUpdate[callId] = { trigger: true, info: "endpoint" + op }; //update last info
                        updateAnalyse = this.analyzeContactChange(jid, phoneNumber, call);//CR27947
                        if (updateAnalyse?.searchOutlookToBeDone && call.contact) { //CR27947
                            updateStatus = await this.telephonyService.updateContactFromOutlookInfos(call.contact, phoneNumber);
                            if (updateStatus) {
                                this.logger.debug("[TelephonySipWiseEventHandler]  update from outlook for contact :" + call.contact.nameForLogs);
                                call.setContact(call.contact); //to recompute avatar?
                                call.setRemoteParty(phoneNumber, jid, call.contact);
                            } else {
                                this.logger.debug("[TelephonyHybridEventHandler] no update from outlook for contact :" + call.contact.nameForLogs);
                            }
                        }
                    } else {
                        this.logger.info("[TelephonySipWiseEventHandler] manageEndPointElem: create endPointId/callId = " + endPointId + "/" + callId + " not yet relevant");
                    }
                    break;
                case "updated":
                    this.logger.info("[TelephonySipWiseEventHandler] manageEndPointElem / updated");
                    await this.endPointElemUpdate(eventElem);
                    if (this.checkCallRelevancy(callId)) {
                        updateAnalyse = this.analyzeContactChange(jid, phoneNumber, call);//CR27947
                        if (updateAnalyse?.searchOutlookToBeDone && call.contact) { //CR27947
                            updateStatus = await this.telephonyService.updateContactFromOutlookInfos(call.contact, phoneNumber);
                            if (updateStatus) {
                                this.logger.debug("[TelephonySipWiseEventHandler]  update from outlook for contact :" + call.contact.nameForLogs);
                                call.setContact(call.contact); //to recompute avatar?
                                call.setRemoteParty(phoneNumber, jid, call.contact);
                            } else {
                                this.logger.debug("[TelephonyHybridEventHandler] no update from outlook for contact :" + call.contact.nameForLogs);
                            }
                        }
                        //CRRAINB-24531++ simulate an hybrid PCGV1 "updatecall" situation for webrtcGatewayService management of update call
                        //callsToUpdate[callId] = { trigger: true, info: "endpoint" + op }; //update last info
                        if (!(call.remoteControled || this.telephonyService.isRemoteControlledPhoneCallId(callId))) {
                            this.sendCallUpdateEvent(this.getCallFromCallId(callId), { actionElemName: "updatecall" });
                        }
                        //CRRAINB-24531--
                    } else {
                        this.logger.info("[TelephonySipWiseEventHandler] manageEndPointElem: update endPointId/callId = " + endPointId + "/" + callId + " not relevant");
                    }
                    break;
                case "ended":
                    this.logger.info("[TelephonySipWiseEventHandler] manageEndPointElem / ended");
                    break;
                default: {
                    this.logger.info("[TelephonySipWiseEventHandler] manageEndPointElem / invalid op -> " + op + " / seqNum = " + seqNum);
                    const errorMessage = "manageEndPointElem / Anomaly unmanaged op : " + op;
                    throw new Error(errorMessage);
                }
            }
        } catch (error) {
            const errorMessage = "manageEndPointElem -- " + error.message;
            throw new Error(errorMessage);
        }
    } //end manageEndPointElem

    /**
     * Method legElemCreate
     * @private
     * @param {any} eventElem [required] xmpp msg, event part
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonySipWiseEventHandler
     */

    private async legElemCreate(eventElem: any): Promise<void> {

        const legElem = eventElem.find("leg");
        try {
            const call: CallRB = await this.getCall(legElem);
            const legState = legElem.attr("state");
            const deviceId = legElem.attr("deviceId");
            const resource = legElem.attr("resource");
            const callerAutoAnswer: boolean = legElem.attr("callerAutoAnswer") === "true";

            if (!deviceId) {
                const errorMessage = "legElemCreate -- Anomaly deviceId missing";
                this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
                throw new Error(errorMessage);
            }
            this.logger.info("[TelephonySipWiseEventHandler] legElemCreate / leg state : " + legState + " / deviceId : " + deviceId);
            call.setDeviceIdSipWise(deviceId);
            call.setRelevantEquipmentId(deviceId);
            const routing: any = this.telephonyService.telephonyServiceSipWise.routingData;
            let device: any;
            call.isSecondary = routing && Boolean(routing.devicesList) && Boolean(device = routing.devicesList.find(elt => elt.deviceId === deviceId)) && device.type === "webrtc";

            if (legState === "initiating" && !call.isOwner && call.getLastCause() === "makeCallWithResource" && resource === this.xmppService.getResourceFromJid(this.contactService.userContact.fullJid)) {
                // External makecall CTI/CPAAS usecase
                // 2 possible usecases:
                //  - auto answer this call
                //  - display a prompt (incoming popup window)

                call.setStatus(CallStatus.RINGING_INCOMMING);
                call.makecallCtiExtern = true;

                const isAutoAnswerMakecallActivated: boolean = (this.settingsService.getSetting("autoAnswer").toString() === "true") && (this.settingsService.getSetting("autoAnswerByDeviceType").toString() === "desktop");
                if (callerAutoAnswer && isAutoAnswerMakecallActivated) {
                    // the call must be autoanswered by our client
                    call.setAutoOffHookResource(resource);
                } else {
                    // generate prompt (incoming popup window)
                    const contact = await this.contactService.getOrCreateContact("", "000");
                    call.setContact(contact);
                }
            } else {
                // standard usecase
                this.callsetStatusFromLegState(call, legState, eventElem);
            }
            //call.setIsVm(false);   RQRAINB-7438
        } catch (error) {
            this.logger.info("[TelephonySipWiseEventHandler] legElemCreate catch");
            const errorMessage = "legElemCreate -- " + error.message;
            this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    } //end legElemCreate

    /**
     * Method legElemUpdate
     * @private
     * @param {any} eventElem [required] xmpp msg, event part
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonySipWiseEventHandler
     */

    private async legElemUpdate(eventElem: any): Promise<void> {
        const legElem = eventElem.find("leg");
        try {
            const call: CallRB = await this.getCall(legElem);
            const legState = legElem.attr("state");
            const deviceId = legElem.attr("deviceId");
            if (deviceId && call && !call.getDeviceIdSipWise()) { //CRRAINB-34356
                this.logger.info("[TelephonySipWiseEventHandler] legElemUpdate / leg state : " + legState + " / deviceId : " + deviceId);
                call.setDeviceIdSipWise(deviceId);
                call.setRelevantEquipmentId(deviceId);
                const routing: any = this.telephonyService.telephonyServiceSipWise.routingData;
                let device: any;
                call.isSecondary = routing && Boolean(routing.devicesList) && Boolean(device = routing.devicesList.find(elt => elt.deviceId === deviceId)) && device.type === "webrtc";
            }
            this.callsetStatusFromLegState(call, legState, eventElem);
        } catch (error) {
            this.logger.info("[TelephonySipWiseEventHandler] legElemUpdate catch");
            const errorMessage = "legElemUpdate -- " + error.message;
            this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
    } //end legElemUpdate

    /**
     * Method endPointElemCreate
     * @private
     * @param {any} eventElem [required] xmpp msg, event part
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonySipWiseEventHandler
     */

    private async endPointElemCreate(eventElem: any): Promise<void> {

        const endPointElem = eventElem.find("endpoint");
        const callId = endPointElem.attr("callId"); //required
        const phoneNumber = endPointElem.find("phoneNumber").text();
        const jid = endPointElem.find("jid").text();
        const firstName = endPointElem.find("firstName").text();
        const lastName = endPointElem.find("lastName").text();
        const displayName = endPointElem.find("displayName").text();
        const type = endPointElem.find("type").text();
        const cause = eventElem.attr("cause");

        if (!endPointElem) {
            const errorMessage = "endPointElemCreate -- Anomaly no endPoint";
            this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
            return;
        }
        if (jid || phoneNumber) {
            const call = this.getCallFromCallId(callId);
            const contact: Contact = await this.endPointContactFromUserIdMng(endPointElem); //RQRAINB-10966++
            if (!contact?.jid && call?.webConferenceRoom) { //specific case of webConfroom
                // if the call is for a room, use the room name and avatar
                contact.updateName("", call.webConferenceRoom.name);
                if (call.webConferenceRoom.avatar) { contact.avatarSrc = call.webConferenceRoom.avatar; }
            } //RQRAINB-10966--

            if (callId && call) {
                call.setEndPointDeviceListSipWise(this.endPointDevicesMng(endPointElem));//RQRAINB-5427
                call.setContact(contact);
                call.setRemoteParty(phoneNumber, jid, contact);
                call.callerNumber = (call.getDirection() === CallDirection.OUTGOING ? this.contactService.userContact.phoneInternalNumber : phoneNumber);
                if (type) {
                    call.distantType = type;
                }
                call.setLastCause(cause);

                if (this.telephonyService.getAutoAnswerCorrelatorIdResourceMap(phoneNumber)) {
                    // hunt group pickup usecase: we prepare to autoanswer the picked-up call
                    this.logger.info(`[TelephonySipWiseEventHandler] endPointElemCreate prepare autoanswer for caller phonenumber ${phoneNumber} resource '${this.telephonyService.getAutoAnswerCorrelatorIdResourceMap(phoneNumber)}'`);
                    call.setAutoOffHookResource(this.telephonyService.getAutoAnswerCorrelatorIdResourceMap(phoneNumber));
                    this.telephonyService.removeAutoAnswerCorrelatorId(phoneNumber);
                }
                this.logger.info("[TelephonySipWiseEventHandler] endPointElemCreate setContact DONE : displayName = " + call.contact.nameForLogs);
            } else {
                this.logger.info("[TelephonySipWiseEventHandler] endPointElemCreate NOT DONE / no associated call");
            }
        } else {
            const errorMessage = "endPointElemCreate -- Anomaly no jid no phoneNumber";
            this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
        }
    } //end endPointElemCreate

    /**
     * Method endPointElemUpdate
     * update the endPoint
     * @private
     * @param {any} eventElem [required] xmpp msg, event part
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @memberof TelephonySipWiseEventHandler
     */

    private async endPointElemUpdate(eventElem: any): Promise<void> {

        const endPointElem = eventElem.find("endpoint");
        const callId = endPointElem.attr("callId"); //required
        const phoneNumber = endPointElem.find("phoneNumber").text();
        const jid = endPointElem.find("jid").text();
        const type = endPointElem.find("type").text();

        if (!endPointElem) {
            const errorMessage = "endPointElemUpdate -- Anomaly no endPoint";
            this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
            return;
        }
        if (jid || phoneNumber) {
            const contact: Contact = await this.endPointContactFromUserIdMng(endPointElem); //RQRAINB-10966
            const call = this.getCallFromCallId(callId);
            if (callId && call) {
                call.setEndPointDeviceListSipWise(this.endPointDevicesMng(endPointElem));//RQRAINB-5427
                call.setContact(contact);
                call.setRemoteParty(phoneNumber, jid, contact);
                call.callerNumber = (call.getDirection() === CallDirection.OUTGOING ? this.contactService.userContact.phoneInternalNumber : phoneNumber);
                if (type) {
                    call.distantType = type;
                }
                this.logger.info("[TelephonySipWiseEventHandler] endPointElemUpdate setContact DONE : displayName = " + call.contact.nameForLogs);
            } else {
                this.logger.info("[TelephonySipWiseEventHandler] endPointElemUpdate setContact NOT DONE / no associated call");
            }
        } else {
            const errorMessage = "endPointElemUpdate -- Anomaly no jid no phoneNumber";
            this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
        }
    } //end endPointElemUpdate

    /**
     * Method endPointDevicesMng
     * @private
     * @param {any} endPointElem [required] xmpp msg endPoint part
     * @returns {any []} list of devices
     * @memberof TelephonySipWiseEventHandler
     */
    private endPointDevicesMng(endPointElem: any): any {

        const callId = endPointElem.attr("callId"); //required
        const endpointId = endPointElem.attr("endpointId");
        const deviceList = [];

        if (callId && endpointId) {
            endPointElem.find("device").each((__index: number, endPointDeviceElem: Element) => {
                const endPointDeviceElemIter = $(endPointDeviceElem);
                const deviceType = endPointDeviceElemIter.attr("type");
                const deviceId = endPointDeviceElemIter.attr("deviceId");
                if (deviceType && deviceId) {
                    deviceList.push({ callId: callId, endpointId: endpointId, deviceType: deviceType, deviceId: deviceId });
                }
            });
        } else {
            const errorMessage = "endPointDevicesMng -- Anomaly no callId or endpointId";
            this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
        }
        return deviceList;
    } //end endPointDevicesMng

    /**
     * Method endPointContactFromUserIdMng
     * @private
     * @param {any} endPointElem [required] xmpp msg endPoint part
     * @async
     * @returns {Promise<Contact>} pomise of contact
     * @memberof TelephonySipWiseEventHandler
     */
    private async endPointContactFromUserIdMng(endPointElem: any): Promise<Contact> { //RQRAINB-10966

        const phoneNumber = endPointElem.find("phoneNumber").text();
        const jid = endPointElem.find("jid").text();
        const firstName = endPointElem.find("firstName").text();
        const lastName = endPointElem.find("lastName").text();
        const displayName = endPointElem.find("displayName").text();
        const origin = endPointElem.find("origin").text(); //RQRAINB-10966
        const userId = endPointElem.find("userId").text(); //RQRAINB-10966

        let contact: Contact = null;

        if (origin && userId) { //RQRAINB-10966
            this.logger.info("[TelephonySipWiseEventHandler] endPointContactFromUserIdMng :  origin/userId = " + origin + " / " + userId);
        }

        if (jid) { //rainbow case
            contact = await this.contactService.getOrCreateContact(jid);
            return contact;
        }
        if (userId && origin && origin !== "RAINBOW") { //userId and origin provided
            try {
                switch (origin) {
                    case "O365":
                        contact = await this.office365AdService.getO365ContactFromServer(userId);
                        break;
                    case "PERSONAL_DIRECTORY":
                    case "BUSINESS_DIRECTORY":
                        contact = await this.companyDirectoryService.getDirectoryContactById(userId);
                        break;

                    case "OUTLOOK":
                        contact = await this.office365AdService.getOutlookContactById(userId);
                        break;

                    case "PBX":
                        contact = null; //nothing new to be expected from pbx server search then manage it from PCG included info as the case of no contact found
                        break;
                    default:
                        this.logger.error("[TelephonySipWiseEventHandler] endPointContactFromUserIdMng anomaly directory origin not managed");
                        contact = null;
                        break;
                }
            } catch (error) { //something wrong with server search
                this.logger.error("[TelephonySipWiseEventHandler] endPointContactFromUserIdMng anomaly directory search error : " + error.status);
                contact = null;
            }
            if (!contact) { //no info from previous search then use provided names from pcg
                contact = await this.contactService.getOrCreateContact(jid, phoneNumber);
                if (firstName || lastName) {
                    const updatedFirstname = firstName ? firstName : '';
                    const updatedLastname = lastName ? lastName : '';
                    contact.updateName(updatedFirstname, updatedLastname);
                }
                else if (displayName) {
                    contact.updateName("", displayName);
                }
                if (contact.temp) {
                    contact.nameUpdatePrio = Contact.NameUpdatePrio.SERVER_UPDATE_PRIO;
                }
            }
            return contact;
        }
        if (phoneNumber) { // manage with the remaining given info
            contact = await this.contactService.getOrCreateContact(null, phoneNumber);
            if (firstName || lastName) {
                const updatedFirstname = firstName ? firstName : '';
                const updatedLastname = lastName ? lastName : '';
                contact.updateName(updatedFirstname, updatedLastname);
            } else if (displayName) { //CRRAINB-26699
                contact.updateName("", displayName);
            }
        }
        return contact;
    } //end endPointContactFromUserIdMng

    /*********************************************************************/
    /** FAILURE STUFF                                                   */
    /*********************************************************************/

    /**
     * Method onFailCallEvent
     * @private
     * @param {CallRB} call [required] the call
     * @param {any} failedElem [required] xmpp msg, fail part
     * @memberof TelephonySipWiseEventHandler
     */
    private onFailCallEvent(call: CallRB, failedElem: any) {

        let cause = failedElem.find("failedReason").text();
        this.logger.info("[TelephonySipWiseEventHandler] onFailCallEvent for callId : " + call.id + " / call status ERROR /" + " cause : " + cause);
        call.setStatus(CallStatus.ERROR);
        if (!this.CallFailureLabels[cause]) { cause = "unknownOverflow"; } //CRRAINB-40662 from NGCP/PCG default value is unknownOverflow
        call.errorMessage = this.CallFailureLabels[cause];
        if (!call.errorMessage) { call.errorMessage = cause; }

        //CRRAINB-35184++
        // It is not a good idea to release the call even when state=failed
        // For instance in case of unknownOverflow (and many of others cases) we must not release
        //
        // setTimeout(() => {
        //     if (call && call.status === CallStatus.ERROR) {
        //         this.telephonyService.releaseCall(call);
        //         //remove call even if release does not work
        //         call.setStatus(CallStatus.ENDED);
        //         this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
        //     }
        // }, 5000);
        //CRRAINB-35184--

    } //end onFailCallEvent

    /*********************************************************************/
    /** PRIVATE UTILITY METHODS                                         */
    /*********************************************************************/

    /**
     * Method convLegStateToCallStatus.
     * conv PCG2 leg state to Call.status
     * @private
     * @param {any} legState [required] the leg state
     * @returns {CallStatus} status
     * @memberof TelephonySipWiseEventHandler
     */
    public convLegStateToCallStatus(legState: any): CallStatus {
        let status: CallStatus;
        if (!legState) {
            this.logger.info("[TelephonySipWiseEventHandler] convLegStateToCallStatus. Anomaly no legState");
            return status;
        }
        switch (legState) {
            case "initiating":
            case "initiated":
                status = CallStatus.DIALING;
                break;
            case "dialing":
                status = CallStatus.DIALING;
                break;
            case "ringingIn":
                status = CallStatus.RINGING_INCOMMING;
                break;
            case "ringingOut":
                status = CallStatus.RINGING_OUTGOING;
                break;
            case "queued":
                status = CallStatus.QUEUED_INCOMMING;
                break;
            case "active":
                status = CallStatus.ACTIVE;
                break;
            case "hold":
                status = CallStatus.HOLD;
                break;
            case "holdVictim":
                status = CallStatus.PUT_ON_HOLD;
                break;
            case "failed":
                status = CallStatus.ERROR;
                break;
            default:
                this.logger.info("[TelephonySipWiseEventHandler] convLegStateToCallStatus. Anomaly legState = " + legState);
                break;
        }
        return status;
    } //end convLegStateToCallStatus.

    /**
     * Method callsetStatusFromLegState
     * set Call.status from PCG2 leg state
     * @private
     * @param {Call} call [required] the call
     * @param {any} legState [required] the leg state
     * @param {any} eventElem [required] xmpp msg, event part
     * @memberof TelephonySipWiseEventHandler
     */
    private callsetStatusFromLegState(call: CallRB, legState: any, eventElem: any) {
        if (!call || !legState) {
            this.logger.info("[TelephonySipWiseEventHandler] callsetStatusFromLegState Anomaly Call = " + call + " legState" + legState);
            return;
        }
        const status = this.convLegStateToCallStatus(legState);
        if (status === CallStatus.ERROR) {
            this.onFailCallEvent(call, eventElem);
        } else {
            call.setStatus(status);
        }
        this.logger.info("[TelephonySipWiseEventHandler] callsetStatusFromLegState legstate = " + legState + " Call.status = " + call.callStatus);
    } //end callsetStatusFromLegState

    /**
     * Method getCallFromCallId
     * @private
     * @param {string} callId [required] the callId
     * @returns {Call} call
     * @memberof TelephonySipWiseEventHandler
     */
    private getCallFromCallId(callId: string): CallRB {
        if (!callId) {
            this.logger.debug("[TelephonySipWiseEventHandler] getCallFromCallId - Anomaly no callId");
            return null;
        }
        const call = this.telephonyService.calls[callId];
        if (call) {
            this.logger.debug("[TelephonySipWiseEventHandler] getCallFromCallId = " + callId + " - " + " call: " + call);
            return call;
        }
        return null;

    }

    /**
     * Method getCall
     * @private
     * @param {any} elem [required] xmpp msg, elt part
     * @async
     * @returns {Promise<CallRB>} Promise resolved with the found call
     * @memberof TelephonySipWiseEventHandler
     */
    private async getCall(elem: any): Promise<CallRB> {
        const callId = elem.attr("callId");
        const legId = elem.attr("legId");
        const call: CallRB = await this.getOrCreateCall(callId, legId);
        return call;
    }

    /**
     * Method getOrCreateCall
     * @private
     * @param {string} callId [required] the call Id
     * @param {any} legId [required] the leg Id
     * @async
     * @returns {Promise<CallRB>} Promise resolved with the found or created call
     * @memberof TelephonySipWiseEventHandler
     */
    private async getOrCreateCall(callId: string, legId: any): Promise<CallRB> {
        if (!callId) {
            const errorMessage = "getOrCreateCall -- Anomaly no callId";
            this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
        let call: CallRB = this.telephonyService.calls[callId];
        if (call?.getConnectionIdSipWise()) { //get call and check that cnxid present
            this.logger.debug("[TelephonySipWiseEventHandler] getOrCreateCall - " + callId + " - " + " => found completed call: " + call);
            return call;
        }
        this.logger.debug("[TelephonySipWiseEventHandler] getOrCreateCall - " + callId + " - " + " => not completed : manage it");
        if (!legId) {
            const errorMessage = "getOrCreateCall -- Anomaly no LegId";
            this.logger.error("[TelephonySipWiseEventHandler] " + errorMessage);
            throw new Error(errorMessage);
        }
        //create
        call = await this.telephonyService.getOrCreateCallSipWise(CallStatus.ENDED, callId, legId);
        return call;
    }

    /**
     * Method sendCallUpdateEvent
     * @private
     * @param {Call} call [required] the call to update
     * @param {any} elem [required] the xmpp element contening the evt info
     * @returns {void} NA
     * @memberof TelephonySipWiseEventHandler
     */
    private sendCallUpdateEvent(call: CallRB, elem: any): void {
        if (call) {
            // keepAlive defence : clear keep alive TO for this call
            if (call.autoClear) {
                clearTimeout(call.autoClear);
                call.autoClear = null;
            }
            this.logger.debug("[TelephonySipWiseEventHandler] sendCallUpdateEvent for call " + call);
            const infoEvt = this.extractInfoEvent(elem);
            this.eventService.publish("ON_CALL_UPDATED_EVENT", call, infoEvt);
        }
    }

    /**
     * Method extractInfoEvent
     * @private
     * @param {any} elem [required] the xmpp element
     * @returns {any} return the info evt
     * @memberof TelephonySipWiseEventHandler
     */
    private extractInfoEvent(elem: any): any {
        let infoEvent = null;
        if (elem && elem !== null) {
            infoEvent = elem;
        }
        return infoEvent;
    }

    /**
     * Method callsUpdate
     * @private
     * @param {Call} callsToUpdate [required] the call to update
     * @returns {void} NA
     * @memberof TelephonySipWiseEventHandler
     */
    private callsUpdate(callsToUpdate: CallRB[], seqNum: string): void {
        this.logger.info("[TelephonySipWiseEventHandler] CallsUpdate seqNum: " + seqNum);
        let sentEvent: boolean = false;
        Object.keys(callsToUpdate || []).forEach((callId) => {
            if (callsToUpdate[callId]?.trigger) {
                //CRRAINB-26056
                const call: CallRB = this.getCallFromCallId(callId);
                if (call && !(call.remoteControled || this.telephonyService.isRemoteControlledPhoneCallId(callId))) {
                    this.logger.info("[TelephonySipWiseEventHandler] [TEL][EVENT] onCallserviceMessageReceived -- seqNum " + seqNum + " -- " + call.callStatus + " -- " + call.id);
                    sentEvent = true;
                    this.sendCallUpdateEvent(call, callsToUpdate[callId].info);
                } else { //CRRAINB-26056 : call is remoteControled (maining handled by another client instance) then do not manage on this client
                    this.telephonyService.clearCall(call, true);
                    if (call && !call.remoteControled) {
                        this.logger.info("[TelephonySipWiseEventHandler] [TEL][EVENT] onCallserviceMessageReceived -- seqNum " + seqNum + " -- Remote Controlled");
                        call.remoteControled = true;
                        this.telephonyService.removeRemoteControlledPhoneCallId(callId);
                    }
                    this.logger.info("[TelephonySipWiseEventHandler] CallsUpdate for callId " + callId + " no triggered because remoteControlled");
                }
            } else {
                this.logger.info("[TelephonySipWiseEventHandler] CallsUpdate for callId " + callId + " no triggered");
                this.logger.info("[TelephonySipWiseEventHandler] [TEL][EVENT] onCallserviceMessageReceived -- seqNum " + seqNum + " -- IGNORED");
            }
        });
        if (!sentEvent) {
            this.logger.info("[TelephonySipWiseEventHandler] [TEL][EVENT] onCallserviceMessageReceived -- seqNum " + seqNum + " -- IGNORED");
        }
    }

    /**
     * Method computeCallRelevancy
     * @private
     * @param {string} deviceId [required] the device Id
     * @param {string} callId optional call id
     * @returns {boolean} true if device Id is the current used device
     * @memberof TelephonySipWiseEventHandler
     */
    private computeCallRelevancy(deviceId: string, callId?: string): boolean {
        let result = false;
        const currentUsedDevice = this.telephonyService.telephonyServiceSipWise.getCurrentDevice();
        if (deviceId && currentUsedDevice) {
            result = (deviceId === currentUsedDevice.deviceId);
        }

        //CR25504-24670 & RQ6572 ++
        // special case for singleStepTransfer with webrtc call and sip call
        // if cause='singleStepTransfer' & <autoOffHookResource>
        // then manage the call even if not for the current device/mode
        if (callId) {
            const call = this.getCallFromCallId(callId);//check if call already existing
            if (call?.getMixtWebTelSingleStepTrans()) {
                result = true;
                this.logger.info("[TelephonySipWiseEventHandler] computeCallRelevancy for deviceId : " + deviceId + " / special mixte (webrtc&sip) transfer");
            }
        }//CR25504-24670 & RQRAINB-6572 --

        this.logger.info("[TelephonySipWiseEventHandler] computeCallRelevancy for deviceId : " + deviceId + " / relevant = " + result);
        return result;
    }

    /**
     * Method checkCallRelevancy
     * @private
     * @param {string} callId [required] the callId
     * @returns {boolean} true if call is relevant
     * @memberof TelephonySipWiseEventHandler
     */
    private checkCallRelevancy(callId: string): boolean {
        let result = false;
        const currentUsedDevice = this.telephonyService.telephonyServiceSipWise.getCurrentDevice();
        const call = this.getCallFromCallId(callId);//check if already existing
        if (call && currentUsedDevice) {
            this.logger.debug("[TelephonySipWiseEventHandler] checkCallRelevancy call.deviceID = " + call.getDeviceIdSipWise());
            result = (call.getDeviceIdSipWise() === currentUsedDevice.deviceId);
        }

        //CR25504-24670 & RQ6572 ++
        // special case for singleStepTransfer with webrtc call and sip call
        // if cause='singleStepTransfer' & <autoOffHookResource>
        // then manage the call even if not for the current device/mode
        if (call?.getMixtWebTelSingleStepTrans()) {
            result = true;
            this.logger.info("[TelephonySipWiseEventHandler] checkCallRelevancy for callId : " + callId + " / special mixte (webrtc&sip) transfer");
        }//CR25504-24670 & RQRAINB-6572 --

        this.logger.info("[TelephonySipWiseEventHandler] checkCallRelevancy for callId : " + callId + " / relevant = " + result);
        return result;
    }

    /**
     * Method analyzeContactChange
     * @private
     * @param {string} jid Jabber identifier of other party
     * @param {string} phoneNumber Phone number of other party
     * @param {Call} call [required] the call to update
     * @returns {any} Object:{ updateContactToBeDone : boolean, searchOutlookToBeDone :boolean}
     *  updateContactToBeDone true if the contact has to be updated in the call (by setContact)
     *  searchOutlookToBeDone true if an outlook search has to be performed to resolve call identity
     * @memberof TelephonySipWiseEventHandler
     */
    private analyzeContactChange(jid: string, phoneNumber: string, call: CallRB): any {

        let updateContact = false;
        let searchOutlook = false;

        if (!jid && !phoneNumber) { //nothing could be analysed then updated
            return null;
        }
        // One2One Call (not a conference) only call.contact is impacted
        if (!call.isConference) {

            // No contact... Whatever situation set contact
            if (!call.contact) {
                return { updateContactToBeDone: true, searchOutlookToBeDone: true };
            }

            // PCG known the distant as a rainbow user (no outlook search is necessary)
            if (jid !== "") {
                // Only change if not the same from previous call setting
                if (call.contact.id !== jid) {
                    updateContact = true;
                    searchOutlook = false;
                }
            }
            // No jid available
            else {
                // call.contact not known as rainbow user, contact.id contain former phoneNumber
                if (call.contact.temp) {
                    if ((call.contact.id !== phoneNumber) //called change
                    ) {
                        updateContact = true;
                        searchOutlook = true;
                    }
                    else if (call.contact.displayName === phoneNumber) { //no change but name not known
                        updateContact = false;
                        searchOutlook = true; //try an outlook research
                    }
                }
                // call.contact known as rainbow user update only if phoneNumber change (except ringing empty case)
                else if (call.getRemotePartyPhoneNumber() !== "" && phoneNumber !== "") {
                    if (call.getRemotePartyPhoneNumber() !== phoneNumber) {
                        updateContact = true;
                        searchOutlook = true;
                    }
                }
            }
            return (updateContact || searchOutlook) ? { updateContactToBeDone: updateContact, searchOutlookToBeDone: searchOutlook } : null;
        }
        return undefined;
    }//end analyzeContactChange
}
